import { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, X, Loader2, LayoutGrid, Layers } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ModuleFormDrawer } from '@/components/admin/ModuleFormDrawer';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SortTh, TableHeadRow } from '@/components/ui/sortable-table';

interface AppModule {
  id: string;
  name: string;
  pageLink: string;
  icon: string;
  type: string;
  parentId: string | null;
  orderNumber: number;
  isActive: boolean;
  parent: { id: string; name: string } | null;
  children: { id: string; name: string; isActive: boolean }[];
}

function ModuleTable({
  modules,
  onEdit,
  onDelete,
  onToggle,
  deletingId,
  startNo,
}: {
  modules: AppModule[];
  onEdit: (m: AppModule) => void;
  onDelete: (m: AppModule) => void;
  onToggle: (m: AppModule) => void;
  deletingId: string | null;
  startNo: number;
}) {
  if (modules.length === 0) return (
    <p className="text-sm text-muted-foreground text-center py-8">No modules found</p>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <TableHeadRow>
            <SortTh label="S.No" sortable={false} className="w-12" />
            <SortTh label="Icon" sortable={false} className="w-16" />
            <SortTh label="Module Name" sortable={false} />
            <SortTh label="Page Link" sortable={false} />
            <SortTh label="Type" sortable={false} />
            <SortTh label="Parent" sortable={false} />
            <SortTh label="Order" sortable={false} className="w-16" />
            <SortTh label="Status" sortable={false} />
            <SortTh label="Actions" sortable={false} />
          </TableHeadRow>
        </thead>
        <tbody>
          {modules.map((mod, idx) => (
            <tr key={mod.id} className="border-b hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 text-slate-500">{startNo + idx}</td>
              <td className="px-4 py-3">
                <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <i className={`${mod.icon} text-blue-600 text-sm`} />
                </div>
              </td>
              <td className="px-4 py-3 font-medium text-slate-900">{mod.name}</td>
              <td className="px-4 py-3">
                <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">{mod.pageLink}</code>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${mod.type === 'M' ? 'bg-violet-100 text-violet-700' : 'bg-orange-100 text-orange-700'}`}>
                  {mod.type === 'M' ? 'Main' : 'Sub'}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-500">{mod.parent?.name || '—'}</td>
              <td className="px-4 py-3 text-slate-600 text-center">{mod.orderNumber}</td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onToggle(mod)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${mod.isActive ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${mod.isActive ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                </button>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onEdit(mod)}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(mod)}
                    disabled={deletingId === mod.id}
                    className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors disabled:opacity-40"
                    title="Delete"
                  >
                    {deletingId === mod.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminModulesPage() {
  const [modules, setModules] = useState<AppModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editModule, setEditModule] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [confirmMod, setConfirmMod] = useState<AppModule | null>(null);
  const [confirmModLoading, setConfirmModLoading] = useState(false);

  const fetchModules = useCallback(async (q = search) => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/admin/modules', { params: q ? { search: q } : {} });
      setModules(data);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load modules');
    }
    finally { setIsLoading(false); }
  }, [search]);

  useEffect(() => { fetchModules(search); }, [search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleToggle = async (mod: AppModule) => {
    // Optimistic flip — revert on failure, same pattern as RolesPage.
    setModules(prev => prev.map(m => m.id === mod.id ? { ...m, isActive: !m.isActive } : m));
    try {
      await api.patch(`/admin/modules/${mod.id}/toggle-active`);
    } catch (err: any) {
      setModules(prev => prev.map(m => m.id === mod.id ? { ...m, isActive: mod.isActive } : m));
      toast('error', err?.response?.data?.message || 'Failed to update module');
    }
  };

  const handleDelete = (mod: AppModule) => setConfirmMod(mod);

  const handleConfirmDelete = async () => {
    if (!confirmMod) return;
    setConfirmModLoading(true);
    setDeletingId(confirmMod.id);
    try {
      await api.delete(`/admin/modules/${confirmMod.id}`);
      setConfirmMod(null);
      fetchModules(search);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete module');
    } finally {
      setDeletingId(null);
      setConfirmModLoading(false);
    }
  };

  const openEdit = (mod: AppModule) => {
    setEditModule(mod);
    setShowDrawer(true);
  };

  const mainModules = modules.filter(m => m.type === 'M');
  const subModules = modules.filter(m => m.type === 'S');

  return (
    <DashboardLayout title="Modules">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Super Admin</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Module Management</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{modules.length} modules · {mainModules.length} main · {subModules.length} sub</p>
          </div>
          <Button onClick={() => { setEditModule(null); setShowDrawer(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Module
          </Button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9 w-64"
              placeholder="Search modules..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <Button type="submit" variant="outline">Search</Button>
          {search && (
            <Button variant="ghost" onClick={() => { setSearch(''); setSearchInput(''); }}>
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </form>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Main Modules */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-violet-600" />
                  Main Modules
                  <span className="ml-auto text-xs font-normal text-muted-foreground">{mainModules.length} modules</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ModuleTable
                  modules={mainModules}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  deletingId={deletingId}
                  startNo={1}
                />
              </CardContent>
            </Card>

            {/* Sub Modules */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="h-4 w-4 text-orange-600" />
                  Sub Modules
                  <span className="ml-auto text-xs font-normal text-muted-foreground">{subModules.length} modules</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ModuleTable
                  modules={subModules}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  deletingId={deletingId}
                  startNo={mainModules.length + 1}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {showDrawer && (
        <ModuleFormDrawer
          module={editModule}
          onClose={() => { setShowDrawer(false); setEditModule(null); }}
          onSaved={() => { setShowDrawer(false); setEditModule(null); fetchModules(search); }}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmMod}
        title="Delete Module?"
        message={`Delete module "${confirmMod?.name}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmMod(null)}
        isLoading={confirmModLoading}
      />
    </DashboardLayout>
  );
}
